import { useState, useEffect } from 'react'
import { useGoogleSheetPayItemConnectionQuery } from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useEditGoogleSheetPayItemConnection } from '../../../../../../hooks/useEditGoogleSheetPayItemConnection'
import { useSyncGoogleSheetPayItemConnection } from '../../../../../../hooks/useSyncGoogleSheetPayItemConnection'
import { useSummaryContext } from '../../../../SummaryContext'
import { useColumns } from './hooks/useColumns'
import { GoogleSheetRecord, GoogleSheetTableProps } from './types'
import RPTPayItemConnectionTable from '../RPTPayItemConnectionTable'

const GoogleSheetTable: React.FC<GoogleSheetTableProps> = ({
  connectionId,
}) => {
  const { globalId } = useSummaryContext()
  const { data, loading, refetch } = useGoogleSheetPayItemConnectionQuery({
    ...DefaultQueryHookOptions,
    variables: globalId ? { connectionId, globalId } : undefined,
    skip: !globalId,
  })
  const [title, setTitle] = useState<string>()
  const [records, setRecords] = useState<GoogleSheetRecord[]>()
  useEffect(() => {
    if (!data?.googleSheetPayItemConnection) return
    const { title, compositeId } = data.googleSheetPayItemConnection
    setTitle(title ?? compositeId)
    const { cells, headers } = data.googleSheetPayItemConnection.records
    setRecords(
      cells.reduce((records, row) => {
        records.push(
          row.reduce((record, cell) => {
            const header = headers.find(({ id }) => id === cell.headerId)
            if (header) record[header.id] = cell
            return record
          }, Object() as GoogleSheetRecord)
        )
        return records
      }, Array<GoogleSheetRecord>())
    )
  }, [data?.googleSheetPayItemConnection])
  const columns = useColumns(
    data?.googleSheetPayItemConnection?.records?.headers
  )
  const sync = useSyncGoogleSheetPayItemConnection({ wait: true })
  const edit = useEditGoogleSheetPayItemConnection()
  return (
    <RPTPayItemConnectionTable
      rowKey={() => crypto.randomUUID()}
      columns={columns}
      records={records}
      refetch={refetch}
      loading={loading}
      headers={data?.googleSheetPayItemConnection?.headers?.map(
        ({ formattedValue: label, ...rest }) => ({ label, ...rest })
      )}
      onSync={(connectionId) =>
        sync({ variables: { input: { connectionId, wait: true } } })
      }
      onEdit={({ connectionId, headers }) =>
        edit({ variables: { input: { connectionId, headers } } })
      }
      connectionName={title}
      additionalCustomHeaderActions={
        data
          ? [
              {
                type: 'externalLink',
                label: title,
                href: data.googleSheetPayItemConnection.url,
              },
            ]
          : undefined
      }
    />
  )
}

export default GoogleSheetTable
